/*
 * Copyright (c) Nexign, JSC, 2021-2024.
 */
/**
 * SideMenu item component
 *
 * @author Yakov Gushchin
 * @author Ilya Baranchikov
 * @version 1.30.0
 * @since 1.11.0
 */

import React, { FC, Fragment, ReactNode, useCallback, useState } from 'react';

import { icons } from '../../../assets';
import { ImgStyled } from '../Block/Img';
import { SideMenuItemChildrenStyled, SideMenuItemStyled } from './styled/SideMenuItemStyled';

export interface ISideMenuItem {
    id: number | string;
    isActive: boolean;
    isOpen?: boolean;
    level: number;
    title: string | ReactNode;
    // callbacks
    onClick?: (id: Pick<ISideMenuItem, 'id'>) => void;
    onWheelClick?: (id: Pick<ISideMenuItem, 'id'>) => void;
    dataName?: string;
}

const SideMenuItem: FC<ISideMenuItem> = (props) => {
    const {
        children,
        id,
        isActive,
        level,
        title,
        // callbacks
        onClick,
        onWheelClick,
        dataName,
    } = props;

    const [isOpen, setOpen] = useState<boolean>(props.isOpen || false);

    /*
     * -------------------------------------------
     *               Handlers
     * -------------------------------------------
     */

    const onSideMenuClickHandler = useCallback(() => {
        onClick?.({ id });
        if (children) {
            setOpen(!isOpen);
        }
    }, [children, id, isOpen, onClick]);

    // wheel click event
    const onSideMenuMouseDownHandler = useCallback(
        (event: React.MouseEvent): void => {
            if (event.button === 1 && onWheelClick) {
                onWheelClick({ id });
            }
        },
        [id, onWheelClick],
    );

    /*
     * -------------------------------------------
     *               Render
     * -------------------------------------------
     */

    return (
        <Fragment>
            <SideMenuItemStyled
                data-name={dataName}
                isActive={isActive}
                isOpen={isOpen}
                onClick={onSideMenuClickHandler}
                onMouseDown={onSideMenuMouseDownHandler}
            >
                {typeof title === 'string' ? <div className="text-block">{title}</div> : title}
                {children && <ImgStyled className="small side-menu__item-icon" src={icons.arrowDown} />}
            </SideMenuItemStyled>
            {isOpen && <SideMenuItemChildrenStyled level={level}>{children}</SideMenuItemChildrenStyled>}
        </Fragment>
    );
};

export default SideMenuItem;
